import { useState, useEffect, useCallback } from "react";

const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Products", href: "#products" },
  { label: "Quality", href: "#quality" },
  { label: "Certifications", href: "#certifications" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Contact", href: "#contact" },
];

declare global {
  interface Window {
    googleTranslateElementInit: () => void;
    google: {
      translate: {
        TranslateElement: new (config: object, el: string) => void;
      };
    };
  }
}

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement(
        {
          pageLanguage: "en",
          includedLanguages:
            "en,hi,bn,ar,fr,de,es,pt,ru,ja,ko,zh-CN,nl,it,tr,id,ms,th,vi,pl",
          layout: 0,
          autoDisplay: false,
        },
        "google_translate_element"
      );
    };
    const script = document.createElement("script");
    script.src =
      "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
    };
  }, []);

  const handleNavClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      e.preventDefault();
      const id = href.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      setMenuOpen(false);
      setActiveSection(id);
    },
    []
  );

  const handleWhatsApp = () => {
    window.open("https://wa.me/917584050023?text=Hello%20Pandit%20Export%2C%20I%20am%20interested%20in%20your%20products.", "_blank");
  };

  return (
    <>
      <style>{`
        #google_translate_element select {
          border: 1px solid #C9A227;
          border-radius: 6px;
          padding: 4px 8px;
          font-size: 13px;
          color: #1B4332;
          background: #fff;
          cursor: pointer;
          outline: none;
        }
        #google_translate_element .goog-te-gadget {
          font-size: 0;
        }
        .goog-te-banner-frame { display: none !important; }
        body { top: 0 !important; }
      `}</style>

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white shadow-md py-2"
            : "bg-transparent py-4"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between">
          {/* Logo */}
          <a
            href="#home"
            onClick={(e) => handleNavClick(e, "#home")}
            className="flex items-center gap-3 flex-shrink-0"
          >
            <img
              src="https://static.readdy.ai/image/57610a2a25a44577dbc5593c17f23216/a21cf814587bced313e3caace3a52e8e.png"
              alt="Pandit Export Logo"
              className="h-16 md:h-20 w-auto object-contain"
            />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap relative group ${
                  scrolled ? "text-[#1B4332]" : "text-white"
                } ${activeSection === link.href.replace("#", "") ? "text-[#C9A227]" : ""}`}
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#C9A227] transition-all duration-300 group-hover:w-full"></span>
              </a>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="hidden lg:flex items-center gap-3">
            <div id="google_translate_element" className="flex items-center"></div>
            <button
              onClick={handleWhatsApp}
              className="flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#1fbb58] transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-base"></i>
              </span>
              WhatsApp Now
            </button>
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`lg:hidden text-2xl cursor-pointer ${scrolled ? "text-[#1B4332]" : "text-white"}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span className="w-8 h-8 flex items-center justify-center">
              <i className={menuOpen ? "ri-close-line" : "ri-menu-line"}></i>
            </span>
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden bg-white shadow-lg border-t border-gray-100 px-4 py-4">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="block py-3 text-[#1B4332] font-medium border-b border-gray-100 last:border-0 hover:text-[#C9A227] transition-colors cursor-pointer"
              >
                {link.label}
              </a>
            ))}
            <div className="flex items-center gap-3 mt-4">
              <div id="google_translate_element_mobile"></div>
              <button
                onClick={handleWhatsApp}
                className="flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-whatsapp-line"></i>
                </span>
                WhatsApp Now
              </button>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
